/**
 * Document Annotation Overlay Component
 * Renders page-native annotations: rectangles, arrows, and labels
 * 
 * Implements evidence-first annotation rendering per spec:
 * - Rectangle must contour the evidence, not the category
 * - Precision scoring determines render eligibility
 * - Structural issues use dashed borders
 */

import React, { useMemo } from 'react';

// Render thresholds - annotations below these scores are not rendered visually
const MIN_RENDER_CONFIDENCE = 0.55;
const MIN_PRECISION_SCORE = 0.70;
const MIN_TEXT_ASSOCIATION_SCORE = 0.70;
const MIN_BOX_WIDTH = 30;
const MIN_BOX_HEIGHT = 12;
const MAX_BOX_WIDTH = 500;  // Reject placeholder regions
const MAX_BOX_HEIGHT = 80;  // Reject section bands

// Spacing and boundary constants
const PAGE_MARGIN = 10;  // Minimum margin from page edge
const MIN_RECT_SPACING = 8;  // Minimum gap between rectangles
const LABEL_PADDING = 6;  // Padding around labels

// Annotation colors - STRONGER RED for readability
const ANNOTATION_COLOR = '#B91C1C';  // Darker, more readable red
const ANNOTATION_COLOR_LABEL = '#991B1B';  // Even darker for labels

/**
 * Validate box dimensions - reject placeholder regions
 */
function isValidBoxSize(width, height) {
  // Reject boxes that are too large (placeholder regions)
  if (width > MAX_BOX_WIDTH || height > MAX_BOX_HEIGHT) {
    return false;
  }
  // Reject boxes that are too small to be real text
  if (width < MIN_BOX_WIDTH || height < MIN_BOX_HEIGHT) {
    return false;
  }
  // Reject boxes with suspicious aspect ratio (too wide = placeholder band)
  const aspectRatio = width / height;
  if (aspectRatio > 20) {  // Way too wide relative to height
    return false;
  }
  return true;
}

/**
 * Clamp a value to stay within page bounds
 */
function clampToPage(value, min, max, pageSize) {
  const minBound = PAGE_MARGIN;
  const maxBound = pageSize - PAGE_MARGIN;
  return Math.max(minBound, Math.min(maxBound - (max - min), value));
}

/**
 * Check if two rectangles overlap
 */
function rectsOverlap(rect1, rect2, spacing = MIN_RECT_SPACING) {
  return !(
    rect1.x + rect1.width + spacing < rect2.x ||
    rect2.x + rect2.width + spacing < rect1.x ||
    rect1.y + rect1.height + spacing < rect2.y ||
    rect2.y + rect2.height + spacing < rect1.y
  );
}

/**
 * Ensure coordinates stay within page bounds
 */
function constrainToPage(coords, pageWidth, pageHeight) {
  const x = Math.max(PAGE_MARGIN, Math.min(pageWidth - PAGE_MARGIN - (coords.width || 50), coords.x || 0));
  const y = Math.max(PAGE_MARGIN, Math.min(pageHeight - PAGE_MARGIN - (coords.height || 20), coords.y || 0));
  const width = Math.min(coords.width || 50, pageWidth - PAGE_MARGIN * 2);
  const height = Math.min(coords.height || 20, pageHeight - PAGE_MARGIN * 2);
  
  return { x, y, width, height };
}

/**
 * Find optimal label position that stays inside page bounds
 * and doesn't overlap with document text
 */
function findLabelPosition(boxX, boxY, boxWidth, boxHeight, labelWidth, labelHeight, pageWidth, pageHeight, existingLabels = []) {
  // Preferred positions: to the right, staying close to the box
  const positions = [
    // Right side, aligned with top of box
    { x: boxX + boxWidth + 8, y: boxY },
    // Right side, slightly lower
    { x: boxX + boxWidth + 8, y: boxY + 5 },
    // Above the box
    { x: boxX, y: boxY - labelHeight - 6 },
    // Below the box
    { x: boxX, y: boxY + boxHeight + 6 },
    // Left side
    { x: boxX - labelWidth - 8, y: boxY },
    // Further right if needed
    { x: boxX + boxWidth + 15, y: boxY + 10 },
  ];
  
  for (const pos of positions) {
    // Check if position is within page bounds
    if (pos.x >= PAGE_MARGIN && 
        pos.x + labelWidth <= pageWidth - PAGE_MARGIN &&
        pos.y >= PAGE_MARGIN && 
        pos.y + labelHeight <= pageHeight - PAGE_MARGIN) {
      
      // Check collision with existing labels
      const labelRect = { x: pos.x, y: pos.y, width: labelWidth, height: labelHeight };
      const hasCollision = existingLabels.some(existing => rectsOverlap(labelRect, existing, 4));
      
      if (!hasCollision) {
        return pos;
      }
    }
  }
  
  // Fallback: fit inside page
  return {
    x: Math.max(PAGE_MARGIN, Math.min(pageWidth - PAGE_MARGIN - labelWidth, boxX + boxWidth + 8)),
    y: Math.max(PAGE_MARGIN, Math.min(pageHeight - PAGE_MARGIN - labelHeight, boxY))
  };
}

/**
 * Determine if an annotation should be rendered visually
 * Evidence-first: only render if we have precise placement
 */
function shouldRenderAnnotation(annotation) {
  const conf = annotation.confidence_score ?? annotation.confidence ?? 0.7;
  const precision = annotation.precision_score ?? 0.85;
  const textAssoc = annotation.text_association_score ?? 0.85;
  
  return (
    annotation &&
    conf >= MIN_RENDER_CONFIDENCE &&
    precision >= MIN_PRECISION_SCORE &&
    textAssoc >= MIN_TEXT_ASSOCIATION_SCORE &&
    annotation.target_region &&
    (annotation.target_region.width || 100) >= MIN_BOX_WIDTH &&
    (annotation.target_region.height || 30) >= MIN_BOX_HEIGHT &&
    annotation.label_text &&
    annotation.reviewer_status !== 'dismissed'
  );
}

/**
 * Get CSS class for annotation scope
 */
function getScopeClass(scope) {
  switch (scope) {
    case 'structural':
      return 'clm-box--structural';
    case 'phrase':
      return 'clm-box--phrase';
    case 'line':
      return 'clm-box--line';
    case 'sentence':
    case 'block':
    default:
      return 'clm-box--clause';
  }
}

/**
 * Get stroke style - DARKER RED for better visibility
 */
function getSeverityColor(severity) {
  // Use darker red for better contrast and readability
  return ANNOTATION_COLOR_LABEL;  // #991B1B - darker red
}

/**
 * Single Annotation Renderer
 * Now includes page boundary checking
 */
const AnnotationItem = ({ 
  annotation, 
  isSelected,
  isHighlighted = false,
  onClick,
  scaleFactor = 1,
  pageWidth = 612,
  pageHeight = 792,
  existingRects = [],
  existingLabels = []
}) => {
  const strokeColor = getSeverityColor(annotation.severity);
  const isStructural = annotation.annotation_scope === 'structural';
  const isLowConfidence = (
    (annotation.confidence_score ?? 0.8) < 0.8 ||
    (annotation.precision_score ?? 0.85) < 0.84 ||
    (annotation.text_association_score ?? 0.85) < 0.86
  );
  
  // Scale coordinates
  const scale = (val) => Math.round((Number(val) || 0) * scaleFactor);
  const scaledPageWidth = scale(pageWidth);
  const scaledPageHeight = scale(pageHeight);
  
  const targetRegion = annotation.target_region || {};
  const labelPos = annotation.label_position || {};
  const arrowFrom = annotation.arrow_from || {};
  const arrowTo = annotation.arrow_to || {};
  
  // Box dimensions - constrain to page bounds
  let rawBoxX = scale(targetRegion.x || 0);
  let rawBoxY = scale(targetRegion.y || 0);
  let rawBoxWidth = Math.max(scale(targetRegion.width || 150), MIN_BOX_WIDTH);
  let rawBoxHeight = Math.max(scale(targetRegion.height || 30), MIN_BOX_HEIGHT);
  
  // BOUNDARY CHECK: Constrain box to page
  const scaledMargin = scale(PAGE_MARGIN);
  const boxX = Math.max(scaledMargin, Math.min(scaledPageWidth - scaledMargin - rawBoxWidth, rawBoxX));
  const boxY = Math.max(scaledMargin, Math.min(scaledPageHeight - scaledMargin - rawBoxHeight, rawBoxY));
  const boxWidth = Math.min(rawBoxWidth, scaledPageWidth - scaledMargin * 2);
  const boxHeight = Math.min(rawBoxHeight, scaledPageHeight - scaledMargin * 2);
  
  // Label text - MULTI-LINE with FULL law citation, NO truncation
  const labelText = annotation.label_text || 'Potential Issue';
  const lawRefs = annotation.law_references || [];
  
  // Parse label into issue text and law citation
  const labelParts = labelText.split(' — ');
  const issueText = labelParts[0] || labelText;
  const lawFromLabel = labelParts[1] || '';
  
  // Build law citation line - NEVER truncate
  let lawCitation = lawFromLabel;
  if (!lawCitation && lawRefs.length > 0) {
    lawCitation = lawRefs.map(ref => ref.replace('FDCPA ', 'FDCPA §').replace('1692', '1692')).join(', ');
  }
  
  // Calculate dimensions for multi-line label
  const fontSize = 11;
  const charWidth = fontSize * 0.52;
  const maxLineWidth = Math.min(180, (scaledPageWidth - boxX - boxWidth - 20));  // Constrain to available space
  const maxCharsPerLine = Math.floor(maxLineWidth / charWidth);
  const lineHeight = fontSize * 1.3;
  
  // Wrap issue text if needed (may need more aggressive wrapping to fit page)
  let issueLine1 = issueText;
  let issueLine2 = '';
  let issueLine3 = '';
  
  if (issueText.length > maxCharsPerLine) {
    const breakPoint = issueText.lastIndexOf(' ', maxCharsPerLine);
    if (breakPoint > maxCharsPerLine * 0.4) {
      issueLine1 = issueText.substring(0, breakPoint);
      const remaining = issueText.substring(breakPoint + 1);
      if (remaining.length > maxCharsPerLine) {
        const breakPoint2 = remaining.lastIndexOf(' ', maxCharsPerLine);
        if (breakPoint2 > 0) {
          issueLine2 = remaining.substring(0, breakPoint2);
          issueLine3 = remaining.substring(breakPoint2 + 1);
        } else {
          issueLine2 = remaining;
        }
      } else {
        issueLine2 = remaining;
      }
    } else {
      issueLine1 = issueText.substring(0, maxCharsPerLine);
      issueLine2 = issueText.substring(maxCharsPerLine);
    }
  }
  
  // Calculate label dimensions
  const numLines = (issueLine2 ? (issueLine3 ? 3 : 2) : 1) + (lawCitation ? 1 : 0);
  const labelWidth = Math.max(
    issueLine1.length * charWidth,
    issueLine2 ? issueLine2.length * charWidth : 0,
    issueLine3 ? issueLine3.length * charWidth : 0,
    lawCitation ? lawCitation.length * charWidth : 0
  ) + 10;
  const labelHeight = lineHeight * numLines + 4;
  
  // BOUNDARY CHECK: Find label position INSIDE page
  const optimalLabelPos = findLabelPosition(
    boxX, boxY, boxWidth, boxHeight, 
    labelWidth, labelHeight, 
    scaledPageWidth, scaledPageHeight,
    existingLabels
  );
  
  const labelX = optimalLabelPos.x;
  const labelY = optimalLabelPos.y;
  
  // Arrow coordinates - connect label to box, staying inside page
  // Arrow starts from left edge of label, ends at right edge of box
  let arrowStartX = labelX - 3;
  let arrowStartY = labelY + labelHeight / 2;
  let arrowEndX = boxX + boxWidth + 2;
  let arrowEndY = boxY + boxHeight / 2;
  
  // If label is to the left of box, flip arrow direction
  if (labelX < boxX) {
    arrowStartX = labelX + labelWidth + 3;
    arrowEndX = boxX - 2;
  }
  // If label is above box
  if (labelY + labelHeight < boxY) {
    arrowStartY = labelY + labelHeight + 2;
    arrowEndY = boxY - 2;
  }
  // If label is below box
  if (labelY > boxY + boxHeight) {
    arrowStartY = labelY - 2;
    arrowEndY = boxY + boxHeight + 2;
  }
  
  // BOUNDARY CHECK: Constrain arrow to page
  arrowStartX = Math.max(scaledMargin, Math.min(scaledPageWidth - scaledMargin, arrowStartX));
  arrowStartY = Math.max(scaledMargin, Math.min(scaledPageHeight - scaledMargin, arrowStartY));
  arrowEndX = Math.max(scaledMargin, Math.min(scaledPageWidth - scaledMargin, arrowEndX));
  arrowEndY = Math.max(scaledMargin, Math.min(scaledPageHeight - scaledMargin, arrowEndY));
  
  const opacity = isLowConfidence ? 0.55 : 1;
  
  // Highlight pulse animation class
  const highlightClass = isHighlighted ? 'annotation-highlight-pulse' : '';
  
  return (
    <g 
      className={`annotation-group ${highlightClass}`}
      onClick={onClick}
      style={{ opacity, cursor: 'pointer' }}
    >
      {/* Highlight glow behind rectangle when locating */}
      {isHighlighted && (
        <rect
          x={boxX - 4}
          y={boxY - 4}
          width={boxWidth + 8}
          height={boxHeight + 8}
          fill="#FBBF24"
          fillOpacity={0.35}
          stroke="#F59E0B"
          strokeWidth={3}
          rx={4}
          ry={4}
          className="annotation-highlight-glow"
        />
      )}
      
      {/* Target rectangle - thinner, cleaner */}
      <rect
        x={boxX}
        y={boxY}
        width={boxWidth}
        height={boxHeight}
        fill={isHighlighted ? 'rgba(251, 191, 36, 0.15)' : 'transparent'}
        stroke={isHighlighted ? '#B45309' : strokeColor}
        strokeWidth={isHighlighted ? 3 : (isStructural ? 1.5 : 2)}
        strokeDasharray={isStructural ? '6,3' : 'none'}
        rx={2}
        ry={2}
        style={{
          filter: isSelected || isHighlighted ? `drop-shadow(0 0 ${isHighlighted ? '8px #F59E0B' : '3px ' + strokeColor + '50'})` : 'none'
        }}
      />
      
      {/* Arrow line - smaller, cleaner */}
      <line
        x1={arrowStartX}
        y1={arrowStartY}
        x2={arrowEndX}
        y2={arrowEndY}
        stroke={strokeColor}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        markerEnd="url(#clmArrowHead-small)"
      />
      
      {/* Label text - MULTI-LINE, PLAIN RED TEXT, NO BOX */}
      {/* Darker red, stronger weight for better readability */}
      {/* Full law citation MUST be visible - NO truncation */}
      <text
        x={labelX}
        y={labelY}
        fill={ANNOTATION_COLOR_LABEL}
        fontSize={fontSize}
        fontWeight="800"
        fontFamily="system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
        style={{ 
          letterSpacing: '0.01em',
          textShadow: '0 0 1px rgba(255,255,255,0.8)'  // Subtle white outline for contrast
        }}
      >
        {/* Line 1: Issue text (first part) */}
        <tspan x={labelX} dy={fontSize}>{issueLine1}</tspan>
        {/* Line 2: Issue text continued (if wrapped) */}
        {issueLine2 && (
          <tspan x={labelX} dy={lineHeight}>{issueLine2}</tspan>
        )}
        {/* Line 3: More text if needed */}
        {issueLine3 && (
          <tspan x={labelX} dy={lineHeight}>{issueLine3}</tspan>
        )}
        {/* Law Citation Line - NEVER truncated, slightly smaller but still readable */}
        {lawCitation && (
          <tspan x={labelX} dy={lineHeight} fontSize={fontSize * 0.95} fontWeight="700">
            {lawCitation}
          </tspan>
        )}
      </text>
    </g>
  );
};

/**
 * Main Document Annotation Overlay
 * Renders all annotations for the current page
 */
const DocumentAnnotationOverlay = ({
  annotations = [],
  pageWidth = 612,
  pageHeight = 792,
  renderWidth,
  renderHeight,
  currentPage = 1,
  selectedAnnotationId = null,
  highlightedFindingId = null,
  onAnnotationClick = () => {},
  showAnnotations = true
}) => {
  // Filter and validate annotations for rendering
  const visibleAnnotations = useMemo(() => {
    if (!showAnnotations) return [];
    
    return annotations
      .filter(a => (a.page_number || 1) === currentPage)
      .filter(shouldRenderAnnotation)
      .sort((a, b) => {
        // Sort by Y position for consistent layering
        const aY = a.target_region?.y || 0;
        const bY = b.target_region?.y || 0;
        return aY - bY;
      });
  }, [annotations, currentPage, showAnnotations]);
  
  if (visibleAnnotations.length === 0) {
    return null;
  }
  
  // Calculate scale factor
  const scaleFactor = renderWidth ? renderWidth / pageWidth : 1;
  const svgWidth = renderWidth || pageWidth;
  const svgHeight = renderHeight || pageHeight;
  
  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      className="absolute top-0 left-0 pointer-events-none"
      style={{ zIndex: 10, overflow: 'visible' }}
      data-testid="annotation-overlay-svg"
    >
      {/* Arrow markers - SMALLER, CLEANER */}
      <defs>
        {/* Small red arrow marker - precise */}
        <marker
          id="clmArrowHead-small"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR_LABEL} />
        </marker>
        <marker
          id="clmArrowHead-red"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR_LABEL} />
        </marker>
        <marker
          id="clmArrowHead-high"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR_LABEL} />
        </marker>
        <marker
          id="clmArrowHead-medium"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR_LABEL} />
        </marker>
        <marker
          id="clmArrowHead-low"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR_LABEL} />
        </marker>
      </defs>
      
      {/* Render annotations with collision tracking and whitespace rejection */}
      <g className="clm-annotations-layer" style={{ pointerEvents: 'all' }}>
        {(() => {
          const existingRects = [];
          const existingLabels = [];
          
          return visibleAnnotations.map((annotation, idx) => {
            const tr = annotation.target_region || {};
            const rect = {
              x: tr.x || 0,
              y: tr.y || 0,
              width: tr.width || 100,
              height: tr.height || 20
            };
            
            // WHITESPACE REJECTION: Skip placeholder regions
            // Boxes that are too large are likely approximate section bands, not exact evidence
            if (!isValidBoxSize(rect.width * scaleFactor, rect.height * scaleFactor)) {
              // This box looks like a placeholder region - skip it
              return null;
            }
            
            // COLLISION CHECK: Skip if this rectangle overlaps an existing one
            const hasCollision = existingRects.some(existing => 
              rectsOverlap(rect, existing, MIN_RECT_SPACING)
            );
            
            if (hasCollision) {
              // Skip - evidence already marked by another finding
              return null;
            }
            
            existingRects.push(rect);
            
            // Check if this annotation should be highlighted
            const annotationId = annotation.id || annotation.finding_id || annotation.normalized_id;
            const shouldHighlight = highlightedFindingId && (
              annotationId === highlightedFindingId ||
              annotation.finding_id === highlightedFindingId ||
              annotation.normalized_id === highlightedFindingId
            );
            
            return (
              <AnnotationItem
                key={annotation.id || `annotation-${idx}`}
                annotation={annotation}
                isSelected={selectedAnnotationId === annotation.id}
                isHighlighted={shouldHighlight}
                onClick={(e) => {
                  e.stopPropagation();
                  onAnnotationClick(annotation);
                }}
                scaleFactor={scaleFactor}
                pageWidth={pageWidth}
                pageHeight={pageHeight}
                existingRects={existingRects}
                existingLabels={existingLabels}
              />
            );
          });
        })()}
      </g>
    </svg>
  );
};

export default DocumentAnnotationOverlay;
